// src/components/Canvas/PreviewCard.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/apiClient';
import RecipePreviewCard from './RecipePreviewCard';

export default function PreviewCard() {
  const { id } = useParams();
  const previewRef = useRef(null);

  const [data, setData] = useState({
    title: 'Untitled',
    category: '',
    servings: '',
    cookingTime: '',
    ingredients: [],
    instructions: [],
    notes: '',
    backgroundImage: '',
    authorName: '',
  });
  const [ready, setReady] = useState(false);
  
  useEffect(() => {
    setReady(false); // ✅ Safe to reset on id change
  }, [id]);

  const loadBackgroundImage = async (designCode) => {
    if (!designCode) return '';
    try {
      const { data: template } = await api.get(`/api/templates/${designCode}`);
      const imagePath = template.backgroundImage || template.previewImage || '';

      if (imagePath) {
        const img = new Image();
        img.onload = () => setReady(true);
        img.onerror = () => setReady(true);
        img.src = `${process.env.REACT_APP_API_BASE_URL}/${imagePath.replace(/^\/+/, '')}`;
      } else {
        setReady(true); // no background to wait for
      }

      return imagePath;
    } catch (err) {
      console.warn('⚠️ Failed to fetch template for preview:', err);
      setReady(true); // fail-safe
      return '';
    }
  };

  useEffect(() => {
    // Expose ref globally so shareUtils.js can access it
    window.__RECIPE_CARD_REF__ = previewRef;
  }, []);

    useEffect(() => {
  const fetchRecipe = async () => {
    try {
      const { data: recipe } = await api.get(`/api/recipes/${id}`);
      // console.log('✅ Loaded recipe from backend:', recipe);

      const ingredients = Array.isArray(recipe.structured_ingredients)
        ? recipe.structured_ingredients.map(ri => ({
            label: ri.name || ri.ingredient?.name || '',
            value: ri.ingredient_id ?? null,
            unit: ri.unit ?? '',
            quantity: parseFloat(ri.quantity) || '',
          }))
        : [];

      const instructions = typeof recipe.instructions === 'string'
        ? recipe.instructions.split('\n')
        : Array.isArray(recipe.instructions)
          ? recipe.instructions
          : [];

      const backgroundImage = await loadBackgroundImage(recipe.design_code);

      setData({
        title: recipe.title || 'Untitled',
        category: recipe.category || '',
        servings: recipe.yield || '',
        cookingTime: recipe.cook_time || '',
        ingredients,
        instructions,
        notes: recipe.notes || '',
        backgroundImage,
        authorName: recipe.signature || '',
      });
    } catch (err) {
      console.error('❌ Failed to load recipe from backend:', err);
    }
  };

  fetchRecipe();
}, [id]);

  if (!ready) {
    return (
      <div className="flex justify-center items-center w-full h-[90vh]">
        <div className="w-[600px] h-[900px] bg-white border border-light-gray shadow rounded-md p-4 animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-3/4" />
          <div className="h-4 bg-slate-100 rounded w-1/2" />
          <div className="h-4 bg-slate-100 rounded w-5/6" />
          <div className="h-4 bg-slate-100 rounded w-4/6" />
          <div className="h-[600px] bg-slate-50 rounded" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 👁️ Visible on-screen preview */}
      <div className="flex justify-center items-center px-2 py-4 w-full">
        <div className="w-full max-w-[600px] aspect-[2/3] relative">
          <RecipePreviewCard {...data} />
        </div>
      </div>

      {/* 🖨️ Hidden export-only version (ref for PDF) */}
      <div
        id="printViewWrapper"
        ref={previewRef}
        style={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          width: '600px',
          height: '900px',
          overflow: 'hidden',
        }}
      >
        <RecipePreviewCard {...data} forceExactSize />
      </div>
    </>
  );
}